/**
 * The idempotency key for one receiving attempt, kept where a reload can find
 * it again.
 *
 * It used to live only in a component ref, which broke the guarantee in both
 * directions. Reloading the page — or reopening a draft after a lost response
 * — minted a fresh key, so the server could not recognise the submission it
 * had already taken and a second partial receipt could be posted against the
 * remaining quantity. And after a receipt failed for good, correcting the
 * inputs on the same screen reused the failed key forever, so every retry
 * came back 409 "sudah pernah gagal" with no way out but a reload.
 *
 * sessionStorage, not localStorage: the key belongs to this tab's attempt at
 * this delivery, and should not outlive the browser session or leak into
 * another tab receiving a different order.
 *
 * Every accessor tolerates storage being unavailable — Safari private mode
 * throws on access — and falls back to a key that simply does not survive a
 * reload, which is where this started.
 */
const PREFIX = 'dr-inbound.attempt.';

function scopeKey(databaseId: string, poNumber: string): string {
  return `${PREFIX}${databaseId}:${poNumber.trim().toUpperCase()}`;
}

/** The current attempt's key for this PO, creating one if there is none. */
export function attemptKey(databaseId: string, poNumber: string): string {
  const name = scopeKey(databaseId, poNumber);
  try {
    const existing = sessionStorage.getItem(name);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(name, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Abandons the current attempt, so the next submission is a new one.
 *
 * Called when the outcome is settled — posted, or failed in a way the server
 * will not reconsider. Never on a network error, where the previous attempt
 * may still have landed and the key is the only thing that can recognise it.
 */
export function clearAttemptKey(databaseId: string, poNumber: string): void {
  try {
    sessionStorage.removeItem(scopeKey(databaseId, poNumber));
  } catch {
    // Nothing to clear if nothing could be stored.
  }
}
